import { useLocalStorage, useWindowSize } from "@vueuse/core";
import { computed, type MaybeRefOrGetter, type Ref, ref, toValue } from "vue";

export const PANE_RESIZE_KEYBOARD_STEP_PX = 24;

interface ResizablePaneOptions {
    storageKey: string;
    defaultWidth: number;
    minWidth: number;
    /** Largest share of the viewport the pane may take; also the "expanded" size. */
    maxViewportFraction: number;
    /** Width of the row the pane sits in (e.g. main content minus the app's left navigation); 0 = unknown. */
    availableWidth?: Ref<number>;
    /** Width to keep free next to the pane (the chat column with its composer, plus any other aside in the row). */
    reservedWidth?: MaybeRefOrGetter<number>;
}

/**
 * Width state for a right-anchored pane with a drag handle on its left edge.
 * The width persists per device; the viewport clamps it so a value saved on a
 * wide screen never overflows a narrower one.
 */
export function useResizablePaneWidth(options: ResizablePaneOptions) {
    const { width: viewportWidth } = useWindowSize();
    const storedWidth = useLocalStorage<number>(options.storageKey, options.defaultWidth);
    const isResizing = ref(false);

    const roomBesideReserved = computed(() => {
        const available = options.availableWidth?.value ?? 0;
        return available > 0 ? available - toValue(options.reservedWidth ?? 0) : Number.POSITIVE_INFINITY;
    });
    const maxWidth = computed(() => Math.max(options.minWidth, Math.min(Math.floor(viewportWidth.value * options.maxViewportFraction), roomBesideReserved.value)));
    /** False when even the minimum width would eat into the reserved column; callers fall back to a dialog. */
    const fits = computed(() => roomBesideReserved.value >= options.minWidth);
    const clamp = (value: number) => Math.round(Math.min(Math.max(value, options.minWidth), maxWidth.value));
    const width = computed(() => clamp(Number.isFinite(storedWidth.value) ? storedWidth.value : options.defaultWidth));
    const isExpanded = computed(() => width.value >= maxWidth.value);
    const style = computed(() => ({ width: `${width.value}px` }));

    const setWidth = (value: number) => {
        storedWidth.value = clamp(value);
    };

    // The default width is not always below the maximum (a narrow row): then "shrink" must still move.
    const narrowWidth = computed(() => Math.max(options.minWidth, Math.min(options.defaultWidth, maxWidth.value - PANE_RESIZE_KEYBOARD_STEP_PX * 4)));
    const toggleExpand = () => {
        setWidth(isExpanded.value ? narrowWidth.value : maxWidth.value);
    };

    const onPointerDown = (event: PointerEvent) => {
        if (event.button !== 0) {
            return;
        }
        event.preventDefault();
        const startX = event.clientX;
        const startWidth = width.value;
        isResizing.value = true;

        const onMove = (move: PointerEvent) => setWidth(startWidth + (startX - move.clientX));
        const onStop = () => {
            isResizing.value = false;
            globalThis.removeEventListener("pointermove", onMove);
            globalThis.removeEventListener("pointerup", onStop);
            globalThis.removeEventListener("pointercancel", onStop);
        };
        globalThis.addEventListener("pointermove", onMove);
        globalThis.addEventListener("pointerup", onStop);
        globalThis.addEventListener("pointercancel", onStop);
    };

    const onKeydown = (event: KeyboardEvent) => {
        const next: Record<string, number> = {
            ArrowLeft: width.value + PANE_RESIZE_KEYBOARD_STEP_PX,
            ArrowRight: width.value - PANE_RESIZE_KEYBOARD_STEP_PX,
            Home: options.minWidth,
            End: maxWidth.value,
        };
        if (!(event.key in next)) {
            return;
        }
        event.preventDefault();
        setWidth(next[event.key]);
    };

    return { width, minWidth: options.minWidth, maxWidth, fits, isExpanded, isResizing, style, setWidth, toggleExpand, onPointerDown, onKeydown };
}
